import { useEffect, useState } from "react";
import { CheckCircle2, ShieldAlert, ShieldCheck } from "lucide-react";
import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { verifyProcurementDocument } from "@/lib/api";
import { configurationReady } from "@/lib/supabase";
import { displayDate, titleCase } from "@/lib/utils";

type Verification = Awaited<ReturnType<typeof verifyProcurementDocument>>;

export function DocumentVerificationPage({ code }: { code: string }) {
  const [result, setResult] = useState<Verification>();
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    if (!configurationReady) {
      setError("Document verification is not configured.");
      return () => {
        live = false;
      };
    }
    verifyProcurementDocument(code)
      .then((value) => {
        if (!live) return;
        if (!value) setError("No sealed RAQOZ document matches this code.");
        else setResult(value);
      })
      .catch((reason: Error) => live && setError(reason.message));
    return () => {
      live = false;
    };
  }, [code]);

  const valid = Boolean(result?.valid);
  return (
    <main className="grid min-h-screen place-items-center bg-[#f7f6f2] p-6 text-[#102733]">
      <article className="w-full max-w-2xl rounded-[2rem] border border-[#dce3df] bg-white p-7 shadow-xl sm:p-10">
        <RaqozLogo />
        {!result && !error && (
          <div className="mt-10">
            <ShieldCheck className="size-10 animate-pulse text-[#0f766e]" />
            <h1 className="mt-5 font-serif text-3xl">Verifying sealed document…</h1>
          </div>
        )}
        {error && (
          <div className="mt-10">
            <ShieldAlert className="size-10 text-red-700" />
            <p className="mt-3 text-xs font-black tracking-[.14em] text-red-700">
              VERIFICATION FAILED
            </p>
            <h1 className="mt-3 font-serif text-3xl">This document could not be verified.</h1>
            <p className="mt-4 text-sm leading-6 text-[#607278]">{error}</p>
          </div>
        )}
        {result && (
          <div className="mt-10">
            {valid ? (
              <CheckCircle2 className="size-10 text-[#0f766e]" />
            ) : (
              <ShieldAlert className="size-10 text-red-700" />
            )}
            <p className={`mt-3 text-xs font-black tracking-[.14em] ${valid ? "text-[#0f766e]" : "text-red-700"}`}>
              {valid ? "VALID SEALED SNAPSHOT" : "INTEGRITY CHECK FAILED"}
            </p>
            <h1 className="mt-3 font-serif text-4xl">
              {titleCase(result.document_kind)}
            </h1>
            <dl className="mt-8 grid gap-4 rounded-2xl bg-[#f2f7f5] p-5 text-sm sm:grid-cols-[10rem_1fr]">
              <dt className="text-[#607278]">Organisation</dt>
              <dd className="font-semibold">{result.organisation_name}</dd>
              <dt className="text-[#607278]">Procurement case</dt>
              <dd className="font-semibold">{result.case_reference}</dd>
              <dt className="text-[#607278]">Document version</dt>
              <dd className="font-semibold">{result.version}</dd>
              <dt className="text-[#607278]">Sealed at</dt>
              <dd className="font-semibold">{displayDate(result.generated_at, true)}</dd>
              <dt className="text-[#607278]">SHA-256</dt>
              <dd className="break-all font-mono text-xs">{result.sha256}</dd>
            </dl>
            <p className="mt-6 text-sm leading-6 text-[#607278]">
              RAQOZ recomputed the stored payload hash and compared it with the
              immutable manifest. This page exposes verification metadata only,
              never the private procurement contents.
            </p>
          </div>
        )}
        <a className="mt-8 inline-flex text-sm font-bold text-[#0f766e] hover:underline" href="/">
          Return to RAQOZ
        </a>
      </article>
    </main>
  );
}
